/**
 * Concrete implementation of Hicom colour picker
 */
class HicomColourPicker {
    private colourPicker: HTMLElement;
    private selectedColourClass: string = "";
    private temporaryColourClass: string = "";
    private hiddenInput: HTMLInputElement;
    private selectedElement: HTMLElement;
    private applyOnClickOutside: boolean = true;

    /**
     * Initializes a new instance of the HicomColourPicker class.
     * @param pickerId The ID of the colour picker element.
     */
    constructor(pickerId: string, applyOnClickOutside: boolean = true) {
        this.colourPicker = document.getElementById(pickerId) as HTMLElement;
        this.selectedElement = this.colourPicker.querySelector(`#selected-color-${pickerId}`) as HTMLElement;
        this.hiddenInput = this.colourPicker.querySelector('input[type="hidden"]') as HTMLInputElement;
        this.applyOnClickOutside = applyOnClickOutside;

        if (this.selectedElement) {
            this.selectedColourClass = Array.from(this.selectedElement.classList).find(className => className.startsWith('cp-')) || "";
        }
        this.temporaryColourClass = this.selectedColourClass;

        this.bindOnChangeEvent();
        this.addEventListeners();
    }

    /**
     * Binds the onChange event to the hidden input element.
     */
    private bindOnChangeEvent(): void {
        const onChangeFuncName = this.colourPicker.getAttribute('data-on-change-func');
        if (onChangeFuncName) {
            try {
                const onChangeFunc = window[onChangeFuncName] as unknown as (event: Event) => void;
                if (typeof onChangeFunc === 'function') {
                    this.hiddenInput.addEventListener('change', onChangeFunc);
                } else {
                    console.warn(`"${onChangeFuncName}" is not a valid function.`);
                }
            } catch (error) {
                console.error(`Error attaching on-change function "${onChangeFuncName}":`, error);
            }
        }
    }

    /**
     * Updates the value of the hidden input element and triggers the change event.
     * @param colorClass The new color class to set.
     */
    private updateHiddenInput(colorClass: string): void {
        if (this.hiddenInput) {
            this.hiddenInput.value = colorClass;
            const changeEvent = new Event('change', {
                bubbles: true,
                cancelable: true,
            });
            this.hiddenInput.dispatchEvent(changeEvent);
        }
    }

    /**
     * Updates the highlight of the selected color button.
     * @param colorClass The color class to highlight.
     */
    private updateSelectedHighlight(colorClass: string): void {
        this.colourPicker.querySelectorAll('.color-button').forEach(button => {
            button.classList.remove('selected-color');
        });
        const selectedButton = this.colourPicker.querySelector(`.color-button[data-color="${colorClass}"]`) as HTMLElement;
        selectedButton?.classList.add('selected-color');
    }

    /**
     * Updates the selected color class of the specified element.
     * @param element The element to update.
     * @param newClass The new color class to set.
     */
    private updateSelectedColorClass(element: HTMLElement, newClass: string): void {
        element.classList.forEach(className => {
            if (className.startsWith('cp-palette-') || className.startsWith('cp-')) {
                element.classList.remove(className);
            }
        });
        if (newClass) {
            element.classList.add(newClass);
        }
    }

    /**
    * Updates the visibility of the text wrapper based on the selected color class.
    */
    private updateTextWrapperVisibility(): void {
        const textWrapper = this.colourPicker.querySelector('.cp-text-wrapper') as HTMLElement;
        const hideLabel = this.colourPicker.getAttribute('data-hide-label') === 'true';
        if (textWrapper && !hideLabel) {
            textWrapper.style.visibility = this.selectedColourClass ? 'hidden' : 'visible';
        }
    }

    /**
     * Adds event listeners to the colour picker element.
     */
    private addEventListeners(): void {
        this.colourPicker.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('color-button')) {
                e.preventDefault();
                const colorClass = target.getAttribute('data-color') as string;
                this.temporaryColourClass = colorClass;
                this.updateSelectedHighlight(colorClass);
                this.updateSelectedColorClass(this.selectedElement, colorClass);
            } else if (target.classList.contains('cancel-selection')) {
                e.preventDefault();
                this.updateSelectedColorClass(this.selectedElement, this.selectedColourClass);
                this.updateSelectedHighlight(this.selectedColourClass);
                this.closeDropdown();
            } else if (target.classList.contains('apply-selection')) {
                e.preventDefault();
                this.selectedColourClass = this.temporaryColourClass;
                this.updateHiddenInput(this.selectedColourClass);
                this.closeDropdown();
            } else if (target.classList.contains('clear-selection')) {
                e.preventDefault();
                this.updateSelectedColorClass(this.selectedElement, '');
                this.updateSelectedHighlight('');
                this.updateHiddenInput('');
                this.selectedColourClass = '';
                this.temporaryColourClass = '';

                this.closeDropdown();
            }
            this.updateTextWrapperVisibility();
        });

        this.colourPicker.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const dropdownMenu = this.colourPicker.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu.contains(target)) {
                e.stopPropagation();
            }
        });

        // Handle the dropdown hidden event to apply or revert colour selection
        this.colourPicker.addEventListener('hidden.bs.dropdown', () => {
            if (this.applyOnClickOutside) {
                this.selectedColourClass = this.temporaryColourClass;
                this.updateHiddenInput(this.selectedColourClass);
            } else {
                this.updateSelectedColorClass(this.selectedElement, this.selectedColourClass);
                this.updateSelectedHighlight(this.selectedColourClass);
            }
        });
    }

    /**
     * Closes the dropdown menu of the colour picker.
     */
    private closeDropdown(): void {
        const dropdownMenu = this.colourPicker.querySelector('.dropdown-menu') as HTMLElement;
        dropdownMenu.classList.remove('show');
        const dropdownButton = this.colourPicker.querySelector('.dropdown-toggle') as HTMLElement;
        dropdownButton.classList.remove('show');
    }
}